import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend 
} from "recharts";
import { Euro, TrendingUp, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#10b981', '#f59e0b'];
const TYPE_COLORS = ['#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function ContratsFinancier() {
  const { token } = useAuth();
  const [financial, setFinancial] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFinancial = async () => {
      if (!token) return;
      setLoading(true);
      
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/contracts/stats/financial`, { headers });
        setFinancial(res.data);
      } catch (error) {
        console.error("Error fetching financial:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFinancial();
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-80 skeleton rounded-lg"></div>
          <div className="h-80 skeleton rounded-lg"></div>
        </div>
      </div>
    );
  }

  const mrrByTypePie = (financial?.mrr_by_type || []).map((item, i) => ({
    ...item,
    color: TYPE_COLORS[i % TYPE_COLORS.length]
  }));

  const abonnementPie = (financial?.abonnement_breakdown || []).map((item, i) => ({
    ...item,
    color: COLORS[i % COLORS.length]
  }));

  return (
    <div className="space-y-6" data-testid="contrats-financier">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Détail Financier</h1>
        <p className="text-muted-foreground text-sm">Analyse financière des contrats</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-white/20">
                <Euro className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm opacity-80">MRR Total</p>
                <p className="text-3xl font-bold">{formatCurrency(financial?.total_mrr || 0)}</p>
                <p className="text-sm opacity-80">par mois</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-white/20">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm opacity-80">CA Leaser Total</p>
                <p className="text-3xl font-bold">{formatCurrency(financial?.total_ca_leaser || 0)}</p>
                <p className="text-sm opacity-80">valeur totale</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* MRR by Type */}
        <Card className="bg-white border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">MRR par Type de Contrat</CardTitle>
          </CardHeader>
          <CardContent>
            {mrrByTypePie.length > 0 ? (
              <div className="flex items-center">
                <div className="w-1/2 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={mrrByTypePie}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="mrr"
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {mrrByTypePie.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-1/2 space-y-2">
                  {mrrByTypePie.map((entry, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-foreground">{entry.type}</span>
                      </div>
                      <span className="font-mono font-medium">{formatCurrency(entry.mrr)}/mois</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>

        {/* Abonnement Breakdown */}
        <Card className="bg-white border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Répartition Abonnement</CardTitle>
          </CardHeader>
          <CardContent>
            {abonnementPie.length > 0 ? (
              <div className="flex items-center">
                <div className="w-1/2 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={abonnementPie}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="montant"
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {abonnementPie.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-1/2 space-y-3">
                  {abonnementPie.map((entry, i) => (
                    <div key={i} className="p-3 rounded-lg" style={{ backgroundColor: `${entry.color}15` }}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-sm text-foreground font-medium">{entry.type}</span>
                      </div>
                      <span className="text-lg font-mono font-bold">{formatCurrency(entry.montant)}/mois</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* CA Leaser by Client */}
      <Card className="bg-white border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Building2 className="w-4 h-4 text-cyan-600" />
            CA Leaser par Client
          </CardTitle>
        </CardHeader>
        <CardContent>
          {financial?.ca_leaser_by_client?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Rang</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Société</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Leaser</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">CA Leaser</th>
                  </tr>
                </thead>
                <tbody>
                  {financial.ca_leaser_by_client.map((client, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-muted-foreground">{i + 1}</td>
                      <td className="py-3 px-4 font-medium text-foreground">{client.societe}</td>
                      <td className="py-3 px-4 text-muted-foreground">{client.leaser || '-'}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-cyan-600">
                        {formatCurrency(client.ca_leaser)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-cyan-50 font-bold">
                    <td colSpan={3} className="py-3 px-4">Total CA Leaser</td>
                    <td className="py-3 px-4 text-right font-mono text-cyan-600">
                      {formatCurrency(financial.total_ca_leaser)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-muted-foreground">
              Aucune donnée de leaser disponible
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
